<template>
  <div class="custom-checkbox" :class="checkboxClasses">
    <v-checkbox
      :input-value="modelValue"
      :indeterminate="indeterminate"
      :disabled="disabled"
      :label="label"
      :hide-details="hideDetails"
      class="checkbox-input"
      :class="checkboxVariantClass"
      @change="onInnerChange"
      @click="handleClick"
      @focus="handleFocus"
      @blur="handleBlur"/>
  </div>
</template>

<script>
export default {
  name: 'Checkbox',
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  emits: ['update:modelValue', 'click', 'focus', 'blur'],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    indeterminate: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'compact'].includes(value),
    },
  },
  data() {
    return {
      isFocused: false,
      isHovered: false,
    };
  },
  computed: {
    checkboxClasses() {
      return {
        'checkbox-focused': this.isFocused,
        'checkbox-hovered': this.isHovered,
        'checkbox-disabled': this.disabled,
        'checkbox-checked': this.modelValue,
        'checkbox-indeterminate': this.indeterminate,
        'checkbox-empty': !this.modelValue && !this.indeterminate,
      };
    },
    checkboxVariantClass() {
      return `checkbox-variant-${this.variant}`;
    },
  },
  methods: {
    onInnerChange(value) {
      this.$emit('update:modelValue', value);
    },
    handleClick(event) {
      this.$emit('click', event);
    },
    handleFocus(event) {
      this.isFocused = true;
      this.$emit('focus', event);
    },
    handleBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
    },
  },
  mounted() {
    const checkboxContainer = this.$el.querySelector('.v-input');
    if (checkboxContainer) {
      checkboxContainer.addEventListener('mouseenter', () => {
        if (!this.disabled) {
          this.isHovered = true;
        }
      });
      checkboxContainer.addEventListener('mouseleave', () => {
        this.isHovered = false;
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

// Vuetify 2: Vue 2.6 / vue-loader 15 — :deep() is unreliable; use ::v-deep (matches rest of project)
$cb-icon: '.v-input--selection-controls__input .v-icon';

.custom-checkbox {
  display: inline-block;

  ::v-deep .v-input.v-input--checkbox {
    margin-top: 0;
    padding-top: 0;

    .v-input__control {
      .v-input__slot {
        display: flex !important;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center !important;
        margin-bottom: 0;
        min-height: 0;

        // Default ripple is positioned for 24px icons; causes offset ring
        .v-input--selection-controls__ripple {
          display: none !important;
        }

        .v-input--selection-controls__input {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          flex-shrink: 0;
          width: 20px !important;
          height: 20px !important;
          min-height: 20px !important;
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }

        .v-input--selection-controls__input .v-icon {
          position: relative;
          flex-shrink: 0;
          align-self: center;
          font-size: 0 !important;
          line-height: 0 !important;
          overflow: hidden;
          width: 18px;
          height: 18px;
          margin: 0 auto;
          border: 1px solid $light-border;
          border-radius: 4px;
          background-color: $light-surface-1;
          color: transparent !important;
          caret-color: transparent !important;
          transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          box-sizing: border-box;

          svg,
          .v-icon__svg,
          .v-icon__component {
            display: none !important;
          }

          &::before {
            content: '' !important;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 0;
            border: 1px solid $light-border;
            border-radius: 4px;
            background-color: $light-surface-1;
            transition: inherit;
            font-size: 0 !important;
            box-shadow: none;
          }
        }
      }
    }

    .v-label {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $body-3-size;
      font-weight: 400;
      line-height: 20px;
      height: auto;
      color: $light-foreground-1;
      margin-left: 0 !important;
      padding-top: 0 !important;
      align-self: center;
      display: inline-flex !important;
      align-items: center !important;
      opacity: 1;
    }
  }

  &.checkbox-empty:not(.checkbox-disabled):not(.checkbox-focused):not(.checkbox-hovered) {
    ::v-deep #{$cb-icon} {
      background-color: $light-surface-1;
      border-color: $light-border;
      color: transparent;

      &::before {
        background-color: $light-surface-1;
        border-color: $light-border;
      }
    }
  }

  &.checkbox-checked:not(.checkbox-disabled):not(.checkbox-indeterminate) {
    ::v-deep #{$cb-icon} {
      background-color: $light-primary !important;
      border-color: $light-primary !important;
      color: transparent !important;

      &::before {
        background-color: $light-primary !important;
        border-color: $light-primary !important;
      }

      &::after {
        content: '✓';
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 1;
        transform: translate(-50%, -50%);
        color: white;
        font-size: $caption-1-size;
        font-weight: bold;
        line-height: 1;
      }
    }
  }

  &.checkbox-indeterminate:not(.checkbox-disabled) {
    ::v-deep #{$cb-icon} {
      background-color: $light-primary !important;
      border-color: $light-primary !important;
      color: transparent !important;

      &::before {
        background-color: $light-primary !important;
        border-color: $light-primary !important;
      }

      &::after {
        content: '−';
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 1;
        transform: translate(-50%, -50%);
        color: white;
        font-size: $body-3-size;
        font-weight: bold;
        line-height: 1;
      }
    }
  }

  &.checkbox-hovered:not(.checkbox-disabled):not(.checkbox-focused) {
    &.checkbox-empty {
      ::v-deep #{$cb-icon} {
        background-color: #E4E5E9;
        border-color: $light-border;

        &::before {
          background-color: #E4E5E9;
          border-color: $light-border;
        }
      }
    }

    &.checkbox-checked:not(.checkbox-indeterminate) {
      ::v-deep #{$cb-icon} {
        background-color: #5CC9BF !important;
        border-color: #5CC9BF !important;

        &::before {
          background-color: #5CC9BF !important;
          border-color: #5CC9BF !important;
        }

        &::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          z-index: 1;
          transform: translate(-50%, -50%);
          color: white;
          font-size: $caption-1-size;
          font-weight: bold;
          line-height: 1;
        }
      }
    }

    &.checkbox-indeterminate {
      ::v-deep #{$cb-icon} {
        background-color: #5CC9BF !important;
        border-color: #5CC9BF !important;

        &::before {
          background-color: #5CC9BF !important;
          border-color: #5CC9BF !important;
        }

        &::after {
          content: '−';
          position: absolute;
          top: 50%;
          left: 50%;
          z-index: 1;
          transform: translate(-50%, -50%);
          color: white;
          font-size: $body-3-size;
          font-weight: bold;
          line-height: 1;
        }
      }
    }
  }

  &.checkbox-focused:not(.checkbox-disabled) {
    ::v-deep #{$cb-icon} {
      // Single ring on the visible box; second shadow on ::before caused misalignment
      box-shadow: 0 0 0 3px #F6A7A9;

      &::before {
        box-shadow: none;
      }
    }
  }

  &.checkbox-disabled {
    ::v-deep #{$cb-icon} {
      opacity: 1;

      &::before {
        opacity: 1;
      }
    }

    ::v-deep .v-label {
      color: #93A0AF !important;
      opacity: 1 !important;
    }

    &.checkbox-empty {
      ::v-deep #{$cb-icon} {
        background-color: #E4E5E9;
        border-color: $light-border;

        &::before {
          background-color: #E4E5E9;
          border-color: $light-border;
        }
      }
    }

    &.checkbox-checked:not(.checkbox-indeterminate) {
      ::v-deep #{$cb-icon} {
        background-color: #F6A7A9 !important;
        border-color: #F6A7A9 !important;

        &::before {
          background-color: #F6A7A9 !important;
          border-color: #F6A7A9 !important;
        }

        &::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          z-index: 1;
          transform: translate(-50%, -50%);
          color: white;
          font-size: $caption-1-size;
          font-weight: bold;
          line-height: 1;
        }
      }
    }

    &.checkbox-indeterminate {
      ::v-deep #{$cb-icon} {
        background-color: #F6A7A9 !important;
        border-color: #F6A7A9 !important;

        &::before {
          background-color: #F6A7A9 !important;
          border-color: #F6A7A9 !important;
        }

        &::after {
          content: '−';
          position: absolute;
          top: 50%;
          left: 50%;
          z-index: 1;
          transform: translate(-50%, -50%);
          color: white;
          font-size: $body-3-size;
          font-weight: bold;
          line-height: 1;
        }
      }
    }
  }

  &.checkbox-variant-compact {
    ::v-deep .v-input.v-input--checkbox {
      .v-input__control .v-input__slot .v-input--selection-controls__input {
        width: 18px !important;
        height: 18px !important;
        min-height: 18px !important;
      }

      .v-input__control .v-input__slot #{$cb-icon} {
        font-size: $body-2-size;
        width: 16px;
        height: 16px;
      }

      .v-label {
        font-size: $caption-1-size;
        line-height: 18px;
      }
    }
  }
}
</style>
